import { useTranslation } from 'react-i18next';
import { localizeGalleryItem, type GalleryItem } from '../data/galleryData';
import GalleryCardImage from './GalleryCardImage';

interface GalleryImageCardProps {
  item: GalleryItem;
  index: number;
  onClick: () => void;
  onLike: () => void;
}

const toolBadgeColors: Record<string, { bg: string; text: string }> = {
  text2img: { bg: '#FF6B9D18', text: '#FF6B9D' },
  img2img: { bg: '#F59E0B18', text: '#F59E0B' },
  edit: { bg: '#10B98118', text: '#10B981' },
  restore: { bg: '#6366F118', text: '#6366F1' },
  tryon: { bg: '#EC489918', text: '#EC4899' },
};

export default function GalleryImageCard({ item, index, onClick, onLike }: GalleryImageCardProps) {
  const { t, i18n } = useTranslation();
  const displayItem = localizeGalleryItem(item, i18n.language);
  const badge = toolBadgeColors[displayItem.toolType] || toolBadgeColors.text2img;
  const isLarge = index % 3 === 0;

  return (
    <div
      className="group relative rounded-2xl overflow-hidden cursor-pointer bg-white"
      style={{
        height: isLarge ? '260px' : index % 5 === 1 || index % 5 === 3 ? '200px' : '240px',
        animation: 'fadeInUp 0.5s ease-out both',
        animationDelay: `${(index % 6) * 0.08}s`,
      }}
      onClick={onClick}
    >
      <GalleryCardImage
        item={displayItem}
        alt={displayItem.prompt.slice(0, 20)}
        className="transition-transform duration-500 group-hover:scale-105"
        width={isLarge ? 640 : 360}
        eager={index < 4}
      />

      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-300" />

      {/* Top badges */}
      <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
          {displayItem.featured && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-400/90 text-white flex items-center gap-0.5">
            <i className="ri-vip-crown-line text-[10px]"></i>
            {t('gallery_featured')}
          </span>
        )}
        <span
          className="px-2 py-0.5 rounded-full text-[10px] font-medium"
          style={{ background: badge.bg, color: badge.text }}
        >
          {displayItem.styleName}
        </span>
      </div>

      {/* Bottom info */}
      <div className="absolute bottom-0 left-0 right-0 p-3 transform translate-y-1 group-hover:translate-y-0 transition-transform duration-300">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0 border border-white/30">
            <img src={displayItem.authorAvatar} alt={displayItem.authorName} className="w-full h-full object-cover object-top" />
          </div>
          <span className="text-white text-xs font-medium truncate">{displayItem.authorName}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <i className="ri-eye-line text-white/70 text-xs"></i>
              <span className="text-white/70 text-xs">{displayItem.views > 9999 ? `${(displayItem.views / 10000).toFixed(1)}w` : displayItem.views}</span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onLike();
              }}
              className="flex items-center gap-1 cursor-pointer active:scale-90 transition-transform"
            >
              <i className={`${displayItem.liked ? 'ri-heart-fill text-pink-400' : 'ri-heart-line text-white/70'} text-xs transition-colors`}></i>
              <span className={`text-xs ${displayItem.liked ? 'text-pink-400' : 'text-white/70'}`}>{displayItem.likes > 9999 ? `${(displayItem.likes / 10000).toFixed(1)}w` : displayItem.likes}</span>
            </button>
          </div>
          <span className="text-white/50 text-[10px]">{displayItem.createdAt}</span>
        </div>
      </div>

      {/* Hover prompt preview */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[85%] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
        <p className="text-white text-xs text-center leading-relaxed line-clamp-3 drop-shadow-lg" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>
          {displayItem.prompt}
        </p>
      </div>
    </div>
  );
}
